import { AGE_BUCKETS } from "@/data/tribes";
import type { BrandAnalysis, BrandOnboardingDna } from "@/hooks/use-brand-branding";
import type { BriefForm, BriefNeeds } from "@/data/brief-forms";

/** Corrections the brand made to what the site analysis said. Kept apart so a new analysis never erases them. */
export interface ProfileOverrides {
  positioning?: string;
  persona?: string;
  territoryPair?: string;
  territoryText?: string;
  competitors?: string[];
  subCategory?: string;
}

export interface EffectiveProfile {
  positioning: string | null;
  persona: string | null;
  territoryPair: string | null;
  territoryText: string | null;
  competitors: string[];
  subCategory: string | null;
}

const clean = (v: string | undefined | null): string | null => (v && v.trim() ? v.trim() : null);

/** What the page shows: the brand's own correction when there is one, otherwise the analysis. */
export function effectiveProfile(
  analysis: BrandAnalysis | null,
  summary: string | null,
  overrides: ProfileOverrides = {},
): EffectiveProfile {
  const a = analysis;
  const analysedCompetitors = Array.isArray(a?.key_competitors) ? a!.key_competitors!.filter(Boolean) : [];
  return {
    positioning: clean(overrides.positioning) ?? clean(a?.positioning_sentence) ?? clean(summary),
    persona: clean(overrides.persona) ?? clean(a?.real_audience) ?? clean(a?.customer_portrait),
    territoryPair: clean(overrides.territoryPair) ?? clean(a?.cultural_territory_pair),
    territoryText: clean(overrides.territoryText) ?? clean(a?.cultural_territory_text) ?? clean(a?.cultural_territory),
    competitors: overrides.competitors ?? analysedCompetitors,
    subCategory: clean(overrides.subCategory) ?? clean(a?.sub_category),
  };
}

export interface CompletenessInput {
  website: string | null;
  profile: EffectiveProfile;
  industries: string[];
  valuesCount: number;
  ageMin: string | null;
  genderFocus: string | null;
  communityActivity: string | null;
}

export interface CompletenessItem {
  key: string;
  label: string;
  done: boolean;
}

/** How complete the profile is, and what is missing, in the order worth fixing first. */
export function profileCompleteness(i: CompletenessInput): { percent: number; items: CompletenessItem[]; missing: CompletenessItem[] } {
  const items: CompletenessItem[] = [
    { key: "website", label: "Website", done: !!clean(i.website) },
    { key: "positioning", label: "What your brand does", done: !!i.profile.positioning },
    { key: "industry", label: "Industry", done: i.industries.length > 0 },
    { key: "values", label: "Brand values", done: i.valuesCount > 0 },
    { key: "audience", label: "Target audience", done: !!i.ageMin && !!i.genderFocus },
    { key: "persona", label: "Target persona", done: !!i.profile.persona },
    { key: "territory", label: "Cultural territory", done: !!(i.profile.territoryPair || i.profile.territoryText) },
    { key: "competitors", label: "Competitors", done: i.profile.competitors.length > 0 },
    { key: "activity", label: "Community partnerships", done: !!clean(i.communityActivity) },
  ];
  const done = items.filter((x) => x.done).length;
  return { percent: Math.round((done / items.length) * 100), items, missing: items.filter((x) => !x.done) };
}

/** The onboarding's gender wording, in the brief form's wording. */
const GENDER_TO_BRIEF: Record<string, string> = {
  "Mostly Women": "Mostly women",
  "Mostly Men": "Mostly men",
  "Equal mix": "Mixed",
};

/** The age buckets from the brand's youngest to its oldest target age. */
export function ageBucketsBetween(min: string | null, max: string | null): string[] {
  const buckets = AGE_BUCKETS as readonly string[];
  const lo = min ? buckets.indexOf(min) : -1;
  const hi = max ? buckets.indexOf(max) : -1;
  if (lo < 0 && hi < 0) return [];
  if (lo < 0) return [buckets[hi]];
  if (hi < 0) return [buckets[lo]];
  return buckets.slice(Math.min(lo, hi), Math.max(lo, hi) + 1);
}

/** Answers a new brief can start with, taken from the brand's profile. Only for fields the form asks. */
export function prefillAudienceFromProfile(form: BriefForm, dna: Pick<BrandOnboardingDna, "ageMin" | "ageMax" | "genderFocus"> | null | undefined): BriefNeeds {
  if (!dna) return {};
  const keys = new Set(form.sections.flatMap((s) => s.fields.map((f) => f.key)));
  const out: BriefNeeds = {};
  const ages = ageBucketsBetween(dna.ageMin, dna.ageMax);
  if (keys.has("ages") && ages.length) out.ages = ages;
  const gender = dna.genderFocus ? GENDER_TO_BRIEF[dna.genderFocus] : undefined;
  if (keys.has("gender") && gender) out.gender = gender;
  return out;
}

export function parseAgeRange(min: string | null, max: string | null): [number, number] | null {
  const extract = (v: string | null) => {
    if (!v) return [];
    const nums = String(v).match(/\d+/g);
    return nums ? nums.map(Number) : [];
  };
  const all = [...extract(min), ...extract(max)];
  if (!all.length) return null;
  return [Math.min(...all), Math.max(...all)];
}

export function genderSplit(
  gender: string | null,
): { female: number; male: number; label: string } | null {
  if (!gender) return null;
  const g = gender.toLowerCase();
  if (g.includes("equal") || g.includes("both") || g.includes("mix") || g.includes("all"))
    return { female: 50, male: 50, label: "All genders" };
  if (g.includes("skew f")) return { female: 65, male: 35, label: "Skews female" };
  if (g.includes("skew m")) return { female: 35, male: 65, label: "Skews male" };
  if (g.includes("female") || g.includes("women"))
    return { female: 75, male: 25, label: "Primarily women" };
  if (g.includes("male") || g.includes("men"))
    return { female: 25, male: 75, label: "Primarily men" };
  return null;
}

/** "25–44 yrs", "~65 yrs", or "70+ yrs": the age range as one readable line. */
export function formatAgeRange(range: [number, number] | null): string | null {
  if (!range) return null;
  const [lo, hi] = range;
  if (lo === hi) return `~${lo} yrs`;
  return `${lo}–${hi >= 70 ? "70+" : hi} yrs`;
}

/** The line under "Complete your profile": what is still missing, or that nothing is. */
export function missingHint(missing: CompletenessItem[], max = 3): string {
  if (missing.length === 0) return "Your profile is complete";
  const shown = missing.slice(0, max).map((m) => m.label.toLowerCase()).join(", ");
  const rest = missing.length - max;
  return `Still missing: ${shown}${rest > 0 ? ` and ${rest} more` : ""}`;
}
